import type { DashboardCard } from "../api/types.ts"
import { useApiQuery } from "../hooks/index.ts"
import { useT } from "../i18n/index.tsx"
import { groupDashboardCards, type DashboardGroupId } from "../lib/dashboard-groups.ts"
import {
  EmptyState,
  ErrorNote,
  PageHeader,
  Skeleton,
  StatCard,
  type IconName,
} from "../ui/index.ts"

const GROUP_TONE: Record<DashboardGroupId, "neutral" | "accent" | "danger"> = {
  urgent: "danger",
  mine: "accent",
  status: "neutral",
}

const GROUP_SIZE: Record<DashboardGroupId, "md" | "lg"> = {
  urgent: "lg",
  mine: "md",
  status: "md",
}

/** 카드 키별 아이콘. 없는 키는 기본값으로 — 서버에 카드가 늘어도 화면이 깨지지 않는다. */
const CARD_ICON: Record<string, IconName> = {
  arrived_today: "inbox",
  overdue: "clock",
  doc_to_confirm: "doc",
  ready_to_ship: "ship",
  arrival_to_confirm: "check",
  disposition_missing: "alert",
  items_missing: "list",
  review_requested: "alert",
}

export function DashboardPage() {
  const t = useT()
  const { data, error } = useApiQuery<{ cards: DashboardCard[] }>("/api/dashboard")
  const cards = data?.cards ?? null

  if (error) return <ErrorNote error={error} />

  const groups = cards ? groupDashboardCards(cards) : []
  const allZero = cards !== null && cards.every((card) => card.count === 0)

  return (
    <>
      <PageHeader title={t("dashboard.title")} description={t("dashboard.description")} />

      {!cards ? (
        <Skeleton lines={5} />
      ) : allZero ? (
        <EmptyState icon="check" title={t("dashboard.empty")} />
      ) : (
        <div className="dash">
          {groups.map((group) => (
            <section className="dash__group" key={group.id}>
              <h2 className="dash__group-title">{t(`dashboard.group.${group.id}`)}</h2>
              <div className={group.id === "urgent" ? "stats stats--lg" : "stats"}>
                {group.cards.map((card) => (
                  <StatCard
                    key={card.key}
                    // 조건은 서버가 준 것을 그대로 넘긴다. 화면이 조건을 다시 만들면
                    // 카드 숫자와 목록 건수가 어긋난다.
                    to={`/cases?${new URLSearchParams(card.query).toString()}`}
                    label={t(`card.${card.key}`)}
                    count={card.count}
                    unit={t("dashboard.unit")}
                    tone={GROUP_TONE[group.id]}
                    size={GROUP_SIZE[group.id]}
                    icon={CARD_ICON[card.key] ?? "list"}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  )
}
